"use client";

import { useMemo, type CSSProperties } from "react";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { FlowList, FlowListRow, Skeleton } from "@/components/desktop/ui";
import { ISLAND_LAYOUT, PHONE_APP_BAR } from "../desktopUI.constants";
import { behindOf, buildFlowRows, foldSites, openOf, type WarehouseFlowCounts, type WarehouseFlowView } from "./warehouseFlow.utils";
import { WarehouseFlowEyebrow, WarehouseFlowLegend } from "./WarehouseFlowHeader";
import { useWarehouseFlowNames } from "./useWarehouseFlowNames";
const PHONE_MAX_SITES = 5;
const PHONE_MAX_ROWS = 8;
const SKELETON_ROW_COUNT = 4;
const SAMPLE_DIM_STYLE: CSSProperties = {
  opacity: 0.5
};
const SCROLL_STYLE: CSSProperties = {
  paddingTop: ISLAND_LAYOUT.CLEARANCE + 8,
  paddingBottom: `calc(${PHONE_APP_BAR.HEIGHT + 24}px + env(safe-area-inset-bottom, 0px))`
};
export function WarehouseFlowPhoneBoard({
  view: fullView,
  sampleNote
}: {
  view: WarehouseFlowView;
  sampleNote?: string;
}) {
  const t = useClientTranslations("desktop.warehouseFlow");
  const isSample = sampleNote !== undefined;
  const dimStyle = isSample ? SAMPLE_DIM_STYLE : undefined;
  const shape = fullView.shape;
  const {
    siteName,
    rowName
  } = useWarehouseFlowNames(shape);
  const view = useMemo(() => foldSites({
    view: fullView,
    maxSites: PHONE_MAX_SITES
  }), [fullView]);
  const rows = useMemo(() => buildFlowRows({
    view,
    maxRows: PHONE_MAX_ROWS
  }), [view]);
  const behindLabel = (counts: WarehouseFlowCounts) => {
    const behind = behindOf(counts);
    return behind > 0 ? t("summary.behind", {
      count: behind
    }) : undefined;
  };
  const subParts = [t("delivered", {
    count: view.totals.stages.delivered
  })];
  if (view.isTruncated) subParts.push(t("truncated", {
    count: view.loads
  }));
  return <div className="pointer-events-auto absolute inset-0 overflow-y-auto overflow-x-hidden overscroll-contain bg-offwhite" data-testid="warehouse-flow-phone" data-sentry-component="WarehouseFlowPhoneBoard" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx">
            <div className="flex min-w-0 flex-col px-4" style={SCROLL_STYLE}>
                <WarehouseFlowEyebrow sampleNote={sampleNote} shouldShowMessage={false} data-sentry-element="WarehouseFlowEyebrow" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                <div className="flex flex-col" style={dimStyle} aria-hidden={isSample || undefined}>
                    <h1 className="mb-2 mt-1.5 text-balance text-figure font-normal leading-[1.1] tracking-[-0.025em] text-ink">
                        {t(`headline.${shape}`, {
            open: openOf(view.totals),
            count: view.counterpartiesWithOpen
          })}{" "}
                        <span className="text-ink-3">
                            {t("behind", {
              count: behindOf(view.totals)
            })}
                        </span>
                    </h1>
                    <span className="text-callout text-ink-2">
                        {subParts.join(" · ")}
                    </span>
                    <WarehouseFlowLegend className="mt-3" view={view} data-sentry-element="WarehouseFlowLegend" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />

                    <span className="mb-1 mt-6 text-xs text-ink-3">
                        {t(`columns.${shape}.sites`)}
                    </span>
                    <FlowList data-sentry-element="FlowList" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx">
                        {view.sites.map(site => <FlowListRow key={site.id} name={siteName(site)} openLabel={t("summary.open", {
            count: openOf(site.counts)
          })} behindLabel={behindLabel(site.counts)} isMuted={site.kind !== "site"} />)}
                    </FlowList>

                    <span className="mb-1 mt-6 text-xs text-ink-3">
                        {t(`columns.${shape}.counterparties`)}
                    </span>
                    <FlowList data-sentry-element="FlowList" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx">
                        {rows.map(row => <FlowListRow key={row.key} name={rowName(row)} place={row.kind === "party" && row.party.name ? row.party.place : null} openLabel={t("summary.open", {
            count: openOf(row.counts)
          })} behindLabel={behindLabel(row.counts)} isMuted={row.kind === "others"} />)}
                    </FlowList>
                </div>
            </div>
        </div>;
}
function SkeletonRows() {
  return <div className="flex flex-col divide-y divide-ink/[0.08]" data-sentry-component="SkeletonRows" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx">
            {Array.from({
      length: SKELETON_ROW_COUNT
    }, (_, index) => <div key={index} className="flex h-10 items-center gap-2.5">
                    <Skeleton className="h-3.5 flex-1" />
                    <Skeleton className="h-3 w-12" />
                    <Skeleton className="h-[22px] w-16 rounded-full" />
                </div>)}
        </div>;
}
export function WarehouseFlowSkeleton() {
  const t = useClientTranslations("desktop.warehouseFlow");
  return <div className="pointer-events-none absolute inset-0 overflow-hidden bg-offwhite" role="status" aria-label={t("loading")} data-sentry-component="WarehouseFlowSkeleton" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx">
            <div className="mx-auto flex max-w-[1180px] flex-col px-4 pt-[64px] md:box-content md:pl-[clamp(32px,4.5vw,64px)] md:pr-[clamp(100px,8vw,128px)] md:pt-[84px]">
                <Skeleton className="h-3 w-24" data-sentry-element="Skeleton" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                <Skeleton className="mt-3 h-8 w-full md:h-[clamp(32px,3.3vw,46px)] md:w-3/5" data-sentry-element="Skeleton" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                <Skeleton className="mt-2 h-8 w-2/3 md:hidden" data-sentry-element="Skeleton" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                <Skeleton className="mt-3 h-3 w-48" data-sentry-element="Skeleton" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                <Skeleton className="mt-4 h-3 w-4/5 md:w-96" data-sentry-element="Skeleton" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                <div className="md:mt-8 md:grid md:grid-cols-[minmax(220px,28%)_minmax(56px,1fr)_minmax(280px,40%)]">
                    <div>
                        <Skeleton className="mb-2 mt-7 h-3 w-28" data-sentry-element="Skeleton" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                        <SkeletonRows data-sentry-element="SkeletonRows" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                    </div>
                    <div className="md:col-start-3">
                        <Skeleton className="mb-2 mt-7 h-3 w-36" data-sentry-element="Skeleton" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                        <SkeletonRows data-sentry-element="SkeletonRows" data-sentry-source-file="WarehouseFlowPhoneBoard.tsx" />
                    </div>
                </div>
            </div>
        </div>;
}